export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = 'substyle_utm';

export function captureUtmsFromUrl(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}

export function persistUtms(utms: UtmParams): void {
  if (Object.keys(utms).length) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utms));
  }
}

export function getStoredUtms(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
}

/** Referrer hostname when navigation came from another site (not same host). */
function crossSiteReferrerHostname(): string | null {
  if (typeof document === 'undefined' || !document.referrer) return null;
  try {
    const ref = new URL(document.referrer);
    if (!ref.hostname || ref.hostname === window.location.hostname) return null;
    return ref.hostname;
  } catch {
    return null;
  }
}

/**
 * Call once on app load: persist UTMs from the URL, and if `utm_source` is
 * absent, set it from the cross-site referrer hostname (also written into the
 * query string via replaceState so the bar matches what we store).
 */
export function initUtmCaptureFromUrl(): void {
  const fromUrl = captureUtmsFromUrl();
  const merged: UtmParams = { ...fromUrl };

  if (!merged.utm_source) {
    const refHost = crossSiteReferrerHostname();
    if (refHost) merged.utm_source = refHost;
  }

  if (Object.keys(merged).length) persistUtms(merged);

  if (merged.utm_source && !fromUrl.utm_source) {
    const url = new URL(window.location.href);
    const existing = url.searchParams.get('utm_source');
    if (!existing) {
      url.searchParams.set('utm_source', merged.utm_source);
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    }
  }
}
